<template>
  <div class="call-activity" :class="{ 'call-activity--auto': isAuto }">
    <div class="call-activity__header">
      <div class="call-activity__title-row">
        <div class="call-activity__icon" :class="`call-activity__icon--${direction}`">
          <BaseIcon :name="directionIcon" size="14" />
        </div>
        <h4 class="call-activity__title">{{ subject }}</h4>
        <Label v-if="outcomeVariant" :variant="outcomeVariant" size="sm" :show-dot="true">
          {{ outcomeLabel }}
        </Label>
      </div>

      <div class="call-activity__meta">
        <span class="call-activity__meta-item">
          <BaseIcon name="calendar" size="12" />
          {{ startedAtLabel }}
        </span>
        <span v-if="durationLabel" class="call-activity__meta-item">
          <BaseIcon name="clock" size="12" />
          {{ durationLabel }}
        </span>
        <span v-if="agentLabel" class="call-activity__meta-item">
          <BaseIcon name="user" size="12" />
          {{ agentLabel }}
        </span>
        <a
          v-if="recordingUrl"
          :href="recordingUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="call-activity__recording"
        >
          <BaseIcon name="play" size="12" />
          {{ $t('call_activity.listen_recording') }}
        </a>
        <span v-if="isAuto" class="call-activity__origin">
          <BaseIcon name="phone" size="12" />
          {{ $t('call_activity.via_phone_system') }}
        </span>
      </div>
    </div>

    <div class="call-activity__body">
      <section v-if="parsed.summary" class="call-activity__section">
        <h5 class="call-activity__section-title">
          {{ $t('call_activity.summary') }}
        </h5>
        <div class="call-activity__section-text">{{ parsed.summary }}</div>
      </section>

      <section v-if="actionItems.length > 0" class="call-activity__section">
        <h5 class="call-activity__section-title">
          {{ $t('call_activity.action_items') }}
        </h5>
        <ul class="call-activity__action-list">
          <li
            v-for="(item, idx) in actionItems"
            :key="idx"
            class="call-activity__action-item"
          >
            {{ item }}
          </li>
        </ul>
      </section>

      <section v-if="parsed.transcript" class="call-activity__section">
        <button
          type="button"
          class="call-activity__transcript-toggle"
          @click="showTranscript = !showTranscript"
        >
          <BaseIcon :name="showTranscript ? 'chevron' : 'chevron'" size="12" :rotate="showTranscript ? '180' : '90'" />
          <span>
            {{ showTranscript ? $t('call_activity.hide_transcript') : $t('call_activity.show_transcript') }}
          </span>
        </button>
        <pre v-if="showTranscript" class="call-activity__transcript">{{ parsed.transcript }}</pre>
      </section>
    </div>
  </div>
</template>

<script>
  import { computed, ref, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Label from '@/components/partials/Label.vue';
  import {
    parseCallDescription,
    formatDuration,
    splitActionItems,
    isTelephonyActivity,
  } from '@/utils/callActivity';

  const OUTCOME_VARIANTS = {
    positive: 'success',
    neutral: 'default',
    negative: 'danger',
    no_answer: 'warning',
  };

  export default {
    name: 'CallActivityCard',

    components: {
      BaseIcon,
      Label,
    },

    props: {
      activity: {
        type: Object,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const showTranscript = ref(false);

      const metadata = computed(() => props.activity?.metadata || {});

      const isAuto = computed(() => isTelephonyActivity(props.activity));

      const direction = computed(() => {
        const dir = metadata.value.direction;
        if (dir === 'inbound' || dir === 'outbound') return dir;
        return 'unknown';
      });

      const directionIcon = computed(() => {
        if (direction.value === 'inbound') return 'arrow-down-right';
        if (direction.value === 'outbound') return 'arrow-up-right';
        return 'phone';
      });

      const subject = computed(() => {
        if (props.activity?.subject) return props.activity.subject;
        if (direction.value === 'inbound') return $t('call_activity.inbound_call');
        if (direction.value === 'outbound') return $t('call_activity.outbound_call');
        return $t('call_activity.call');
      });

      const startedAtLabel = computed(() => {
        const value = metadata.value.started_at || props.activity?.created_at;
        if (!value) return '';
        return dayjs(value).format('DD MMM HH:mm');
      });

      const durationLabel = computed(() => {
        const seconds = metadata.value.duration_seconds;
        if (seconds == null || seconds === '') return '';
        return formatDuration(seconds);
      });

      const agentLabel = computed(() => {
        const user = props.activity?.assigned_user;
        if (user?.first_name || user?.last_name) {
          return [user.first_name, user.last_name].filter(Boolean).join(' ');
        }
        return metadata.value.agent_email || '';
      });

      const recordingUrl = computed(() => metadata.value.recording_url || '');

      const outcomeVariant = computed(() => OUTCOME_VARIANTS[props.activity?.outcome] || null);

      const outcomeLabel = computed(() => {
        const key = props.activity?.outcome;
        if (!key) return '';
        const translated = $t(`deals.outcomes.${key}`);
        return translated && translated !== `deals.outcomes.${key}` ? translated : key;
      });

      const parsed = computed(() => parseCallDescription(props.activity?.description));

      const actionItems = computed(() => splitActionItems(parsed.value.actionItems));

      return {
        showTranscript,
        isAuto,
        direction,
        directionIcon,
        subject,
        startedAtLabel,
        durationLabel,
        agentLabel,
        recordingUrl,
        outcomeVariant,
        outcomeLabel,
        parsed,
        actionItems,
      };
    },
  };
</script>

<style scoped lang="scss">
  .call-activity {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    overflow: hidden;

    &--auto {
      border-left: 3px solid var(--brand-400);
    }

    &__header {
      padding: 0.875rem 1rem;
      border-bottom: 1px solid var(--slate-100);
      background: var(--slate-50);
    }

    &__title-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      flex-wrap: wrap;
    }

    &__icon {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--slate-200);
      color: var(--slate-600);

      &--inbound {
        background: var(--green-100);
        color: var(--green-700);
      }
      &--outbound {
        background: var(--blue-100);
        color: var(--blue-700);
      }
    }

    &__title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
      flex: 1;
      min-width: 0;
    }

    &__meta {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      flex-wrap: wrap;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__meta-item {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;

      .icon {
        color: var(--slate-400);
      }
    }

    &__recording {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      color: var(--brand-600);
      text-decoration: none;
      font-weight: 500;

      @include hover {
        color: var(--brand-700);
        text-decoration: underline;
      }
    }

    &__origin {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.125rem 0.5rem;
      border-radius: 0.375rem;
      background: var(--brand-50);
      color: var(--brand-700);
      font-weight: 500;
    }

    &__body {
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__section-title {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 0 0 0.375rem 0;
    }

    &__section-text {
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--slate-800);
      white-space: pre-wrap;
    }

    &__action-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__action-item {
      position: relative;
      padding-left: 1.125rem;
      font-size: 0.875rem;
      line-height: 1.4;
      color: var(--slate-800);

      &::before {
        content: '';
        position: absolute;
        left: 0.375rem;
        top: 0.6em;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: var(--brand-500);
      }
    }

    &__transcript-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      background: none;
      border: none;
      padding: 0;
      color: var(--slate-600);
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;

      @include hover {
        color: var(--slate-800);
      }
    }

    &__transcript {
      margin: 0.5rem 0 0 0;
      padding: 0.75rem;
      background: var(--slate-900);
      color: var(--slate-100);
      border-radius: 0.375rem;
      font-size: 0.75rem;
      line-height: 1.5;
      max-height: 320px;
      overflow: auto;
      white-space: pre-wrap;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
  }
</style>
